import { FC, ReactNode } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	Stack,
	Typography
} from '@mui/material';
import WarningOutlined from '@ant-design/icons/WarningOutlined';
import Avatar from '@/components/@extended/Avatar';
import { PopupTransition } from '@/components/@extended/Transitions';

type ConfirmColor = 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';

interface IConfirmDialogProps {
	open: boolean;
	title: ReactNode;
	description?: ReactNode;
	confirmText?: string;
	cancelText?: string;
	color?: ConfirmColor;
	icon?: ReactNode;
	loading?: boolean;
	onConfirm: () => void;
	onClose: () => void;
}

// Reusable confirm dialog (mirrors the template's ConfirmDialog):
// centered colored avatar + icon, title, description, two full-width buttons.
export const ConfirmDialog: FC<IConfirmDialogProps> = ({
	open,
	title,
	description,
	confirmText = 'Confirmă',
	cancelText = 'Anulează',
	color = 'warning',
	icon,
	loading = false,
	onConfirm,
	onClose
}) => (
	<Dialog
		open={open}
		onClose={onClose}
		keepMounted
		fullWidth
		maxWidth="xs"
		slots={{ transition: PopupTransition }}
	>
		<DialogContent sx={{ p: 3 }}>
			<Stack sx={{ gap: 2.5, alignItems: 'center' }}>
				<Avatar color={color} sx={{ width: 56, height: 56, fontSize: '1.35rem' }}>
					{icon ?? <WarningOutlined />}
				</Avatar>
				<Stack sx={{ gap: 1, width: 1 }}>
					<Typography variant="h4" sx={{ textAlign: 'center' }}>
						{title}
					</Typography>
					{description && (
						<Box
							sx={{ textAlign: 'center', color: 'text.secondary', typography: 'body1' }}
						>
							{description}
						</Box>
					)}
				</Stack>
				<Stack direction="row" sx={{ gap: 1.5, width: 1, mt: 0.5 }}>
					<Button
						fullWidth
						color="secondary"
						variant="outlined"
						onClick={onClose}
						disabled={loading}
					>
						{cancelText}
					</Button>
					<Button
						fullWidth
						color={color}
						variant="contained"
						onClick={onConfirm}
						disabled={loading}
						autoFocus
					>
						{loading ? <CircularProgress size={20} color="inherit" /> : confirmText}
					</Button>
				</Stack>
			</Stack>
		</DialogContent>
	</Dialog>
);

ConfirmDialog.defaultProps = {
	description: undefined,
	confirmText: 'Confirmă',
	cancelText: 'Anulează',
	color: 'warning',
	icon: undefined,
	loading: false
};
